import { AfterContentInit, ChangeDetectorRef, Directive, ElementRef, Input, OnDestroy, inject } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatButton, MatFabButton, MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatInput } from '@angular/material/input';
import { MatRadioGroup } from '@angular/material/radio';
import { MatSelect } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { PermissionExpressionService } from '@shared/services/permission-expression.service';
import { UserPermissionsService } from '@shared/services/userpermissions.service';
import { Subject, takeUntil } from 'rxjs';

const SELF = { optional: true, self: true } as const;

// Disables (greys out) the host when the user lacks permission.
@Directive({
	selector: '[appDisableWithoutPermission]',
	standalone: false,
})
export class DisableWithoutPermissionDirective implements AfterContentInit, OnDestroy {
	@Input() appDisableWithoutPermission: string | string[];

	private readonly userPermissionsService = inject(UserPermissionsService);
	private readonly permissionExpressionService = inject(PermissionExpressionService);
	private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
	private readonly changeDetectorRef = inject(ChangeDetectorRef);
	private readonly ngControl = inject(NgControl, SELF);
	private readonly matInput = inject(MatInput, SELF);
	private readonly matSelect = inject(MatSelect, SELF);
	private readonly matCheckbox = inject(MatCheckbox, SELF);
	private readonly matSlideToggle = inject(MatSlideToggle, SELF);
	private readonly matRadioGroup = inject(MatRadioGroup, SELF);
	private readonly matButton = inject(MatButton, SELF);
	private readonly matIconButton = inject(MatIconButton, SELF);
	private readonly matFabButton = inject(MatFabButton, SELF);
	private readonly matMiniFabButton = inject(MatMiniFabButton, SELF);

	private readonly destroy$ = new Subject<void>();

	// Deliberately not the constructor: FormControlName only attaches its control in ngOnChanges,
	// so ngControl.control is still null when the directive is constructed.
	ngAfterContentInit(): void {
		this.userPermissionsService.permissions$.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateDisabledState());
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private updateDisabledState(): void {
		const disabled = !this.permissionExpressionService.hasPermission(this.appDisableWithoutPermission);

		if (this.ngControl?.control) {
			disabled ? this.ngControl.control.disable() : this.ngControl.control.enable();
		}

		if (this.matInput) {
			this.matInput.disabled = disabled;
			this.matInput.stateChanges.next();
		}
		this.matSelect?.setDisabledState(disabled);
		if (this.matCheckbox) {
			this.matCheckbox.disabled = disabled;
		}
		if (this.matSlideToggle) {
			this.matSlideToggle.disabled = disabled;
		}
		if (this.matRadioGroup) {
			this.matRadioGroup.disabled = disabled;
		}

		const button = this.matButton ?? this.matIconButton ?? this.matFabButton ?? this.matMiniFabButton;
		if (button) {
			button.disabled = disabled;
		}

		const hasMaterial = this.matInput || this.matSelect || this.matCheckbox || this.matSlideToggle || this.matRadioGroup || button;
		if (!hasMaterial) {
			this.elementRef.nativeElement.toggleAttribute('disabled', disabled);
		}

		this.changeDetectorRef.markForCheck();
	}
}
